import { onCLS, onINP, onLCP, onFCP, onTTFB } from 'web-vitals';

type MetricHandler = (metric: {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType: string;
}) => void;

/**
 * Reports Core Web Vitals to analytics
 * INP (Interaction to Next Paint) replaces FID as of March 2024
 * 
 * Thresholds:
 * - LCP: <2.5s (good), 2.5-4s (needs improvement), >4s (poor)
 * - INP: <200ms (good), 200-500ms (needs improvement), >500ms (poor)
 * - CLS: <0.1 (good), 0.1-0.25 (needs improvement), >0.25 (poor)
 */
export function reportWebVitals(onPerfEntry?: MetricHandler) {
  if (!onPerfEntry || typeof onPerfEntry !== 'function') {
    return;
  }

  // Core Web Vitals (ranking factors)
  onCLS(onPerfEntry); // Cumulative Layout Shift
  onINP(onPerfEntry); // Interaction to Next Paint (replaced FID in March 2024)
  onLCP(onPerfEntry); // Largest Contentful Paint
  
  // Additional metrics for monitoring
  onFCP(onPerfEntry); // First Contentful Paint
  onTTFB(onPerfEntry); // Time to First Byte
}

/**
 * Send metrics to Google Analytics 4
 */
export function sendToGoogleAnalytics(metric: any) {
  // Only send metrics if gtag is available
  if (typeof window !== 'undefined' && window.gtag) {
    // Send to Google Analytics with proper event naming
    const eventName = `web_vitals_${metric.name.toLowerCase()}`;
    
    window.gtag('event', eventName, {
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      metric_rating: metric.rating,
      metric_delta: Math.round(metric.delta),
      metric_id: metric.id,
      navigation_type: metric.navigationType || 'unknown',
      // Custom dimensions for better analysis
      page_path: window.location.pathname,
      page_title: document.title,
    });

  }
}


/**
 * Performance budget checker
 * Logs warnings when metrics exceed thresholds
 */
export function checkPerformanceBudget(metric: any) {
  const budgets = {
    LCP: 2500,  // 2.5 seconds
    INP: 200,   // 200 milliseconds (March 2024 Core Web Vital)
    CLS: 0.1,   // 0.1 score
    FCP: 1800,  // 1.8 seconds
    TTFB: 800,  // 800 milliseconds
  };

  const budget = budgets[metric.name as keyof typeof budgets];
  
  if (budget && metric.value > budget) {
    
    // Send budget violation event to analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'performance_budget_exceeded', {
        metric_name: metric.name,
        metric_value: Math.round(metric.value),
        budget_value: budget,
        page_path: window.location.pathname,
      });
    }
  }
}

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}